import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { registerFirm } from '../utils/apiUtils';
import PasswordInput from '../components/PasswordInput';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { validateField, isValidPassword, isValidPhone } from '../utils/validationUtils';
import { getUserReadableError } from '../utils/utils';
import { useDispatch } from 'react-redux';
import { loginUser, validateUser } from '../redux/authSlice';
import AuthLayout from '../components/AuthLayout';

class RegisterStep2 extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      formData: {
        firstName: '',
        lastName: '',
        firmName: '',
        phone: '',
        password: '',
        timezone: 'America/New_York',
        dateFormat: 'MM/DD/YYYY',
      },
      fieldErrors: {},
      isLoading: false,
    };
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
      fieldErrors: {
        ...prevState.fieldErrors,
        [name]: undefined,
      },
    }));
  };

  onSubmit = async (e) => {
    const code = new URLSearchParams(this.props.location.search).get('code');
    e.preventDefault();

    const fieldErrors = {};
    let error = validateField({ isRequired: true, maxLength: 50 }, this.state.formData.firstName, 'First name');
    if (!!error) fieldErrors.firstName = error;
    error = validateField({ isRequired: true, maxLength: 50 }, this.state.formData.lastName, 'Last name');
    if (!!error) fieldErrors.lastName = error;
    error = validateField({ isRequired: true, maxLength: 100 }, this.state.formData.firmName, 'Business / Company name');
    if (!!error) fieldErrors.firmName = error;

    const trimmedPhone = this.state.formData.phone.trim();
    if (trimmedPhone && !isValidPhone(trimmedPhone)) {
      fieldErrors.phone = 'Please enter a valid phone number';
    }

    error = validateField({ isPassword: true }, this.state.formData.password, 'Password');
    if (!!error) {
      fieldErrors.password = error;
    } else if (!isValidPassword(this.state.formData.password)) {
      fieldErrors.password = 'Invalid password';
    }

    if (Object.keys(fieldErrors).length > 0) {
      this.setState({ fieldErrors });
      showErrorMessage(Object.values(fieldErrors)[0]);
      return;
    }

    this.setState({ fieldErrors: {}, isLoading: true });
    try {
      const registerResponse = await registerFirm({
        ...this.state.formData,
        phone: trimmedPhone || null,
        confirmPassword: this.state.formData.password,
        privacyPolicyAccepted: true,
        code,
      });
      showSuccessMessage('Account verified successfully 🎉');

      const pendoParams = '?pendo=LA7k-x1xoQDk3OBPh1pRGoe5kU0&step=Z6U9lrOAqIVyLsjvA6_8071mRGY';
      const email = registerResponse.data?.user?.email;
      const { password } = this.state.formData;

      if (email && password) {
        await this.props.dispatch(loginUser({ email, password }, this.props.navigate, () => true));
        const result = await this.props.dispatch(validateUser());

        if (validateUser.fulfilled.match(result)) {
          this.props.navigate('/onboarding' + pendoParams);
        } else {
          console.error('validateUser failed after registration:', result.error);
          this.props.navigate('/onboarding' + pendoParams);
        }
      } else {
        this.props.navigate('/onboarding' + pendoParams);
      }
    } catch (error) {
      console.error('RegisterStep2 failed:', error);
      const errorMessage = getUserReadableError(error) || error.message;
      showErrorMessage(errorMessage);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { formData, fieldErrors, isLoading } = this.state;

    return (
      <AuthLayout
        icon={<img src="/images/logo_trans.png" alt="TypeHero" className="auth-logo" />}
        title="Almost there — last step"
      >
        <form onSubmit={this.onSubmit} className="auth-form">
          <div className="input-row" style={{ gap: '12px' }}>
            <div className="input-group">
              <label htmlFor="firstName" className="input-label">First name<span className="required">*</span></label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={this.handleInputChange}
                autoComplete="given-name"
                className="input-field"
                aria-invalid={!!fieldErrors.firstName}
                required
              />
              {fieldErrors.firstName && <p className="auth-validation-error">{fieldErrors.firstName}</p>}
            </div>

            <div className="input-group">
              <label htmlFor="lastName" className="input-label">Last name<span className="required">*</span></label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={this.handleInputChange}
                autoComplete="family-name"
                className="input-field"
                aria-invalid={!!fieldErrors.lastName}
                required
              />
              {fieldErrors.lastName && <p className="auth-validation-error">{fieldErrors.lastName}</p>}
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="firmName" className="input-label">Business / Company name<span className="required">*</span></label>
            <input
              type="text"
              id="firmName"
              name="firmName"
              value={formData.firmName}
              onChange={this.handleInputChange}
              autoComplete="organization"
              className="input-field"
              aria-invalid={!!fieldErrors.firmName}
              required
            />
            {fieldErrors.firmName && <p className="auth-validation-error">{fieldErrors.firmName}</p>}
            <p className="auth-helper-text">Used for billing — you can change it later.</p>
          </div>

          <div className="input-group">
            <label htmlFor="phone" className="input-label">Phone number (optional)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={this.handleInputChange}
              autoComplete="tel"
              className="input-field"
              aria-invalid={!!fieldErrors.phone}
            />
            {fieldErrors.phone && <p className="auth-validation-error">{fieldErrors.phone}</p>}
          </div>

          <div className="input-group">
            <label htmlFor="password" className="input-label">Password<span className="required">*</span></label>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={this.handleInputChange}
              placeholder="Create a strong password"
              showStrength={true}
              autoComplete="new-password"
              required
            />
            {fieldErrors.password && <p className="auth-validation-error">{fieldErrors.password}</p>}
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="auth-button"
              disabled={isLoading}
            >
              {isLoading ? 'Setting up...' : 'Enter TypeHero'}
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }
}

function withRouter(Component) {
  return function WrappedComponent(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    return <Component {...props} navigate={navigate} location={location} dispatch={dispatch} />;
  };
}

export default withRouter(RegisterStep2);